/// <amd-module name="CodeCoverage/CodeCoverageSignalR/CodeCoverageHub" />
import {
    getCodeCoverageSignalRHubProxyToRegister,
    HubNames,
    ICodeCoverageSignalRHubClient,
    ICodeCoverageSignalRHubServer
} from "CodeCoverage/CodeCoverageSignalR/CodeCoverageHubProxies";
import { IHubHostInformation, IHubListener, SignalRHub } from "VSS/Features/SignalR/Hub";
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPageService } from "VSS/Platform/Page";

export interface ICodeCoverageSignalRHubOptions {
    hostInformation: IHubHostInformation;
    projectId: string;
    notificationSync: ICodeCoverageHubSignalRSync;
    listener?: IHubListener;
}

export interface ICodeCoverageHubSignalRSync {
    codeCoverageStatsChangedForBuild: (buildId: number) => void;
}

export interface ISignalRUrlData {
    connectionUrl: string;
}

export interface ICodeCoverageSignalRHubService extends IVssService {
    initialize: (pageContext: IVssPageContext, projectId: string, notificationSync: ICodeCoverageHubSignalRSync) => Promise<void>;
    watchBuild: (buildId: number) => Promise<void>;
    unwatchBuild: (buildId: number) => Promise<void>;
    dispose: () => void;
}

export class CodeCoverageSignalRHub extends SignalRHub<ICodeCoverageSignalRHubClient, ICodeCoverageSignalRHubServer> {
    private static s_userInactivityTimeoutMs = 1 * 60 * 60 * 1000;
    private _options: ICodeCoverageSignalRHubOptions;
    private _watchedBuilds: Set<number>;
    private _started: boolean;

    constructor(options: ICodeCoverageSignalRHubOptions) {
        super({
            hostInformation: options.hostInformation,
            name: HubNames.codeCoverageHub,
            listener: options.listener,
            userInactivityTimeoutMs: CodeCoverageSignalRHub.s_userInactivityTimeoutMs
        });

        this._options = options;
        this._watchedBuilds = new Set();
        this.initializeProxy(getCodeCoverageSignalRHubProxyToRegister, this._initializeClient);
    }

    public watchBuild(buildId: number): Promise<void> {
        if (this._watchedBuilds.has(buildId)) {
            Promise.resolve();
        }
        return new Promise<void>((resolve, reject) => {
            this.start().then(() => {
                const hub = this.hub;
                if (hub) {
                    hub.server.watchBuild(buildId).then(() => {
                        this._watchedBuilds.add(buildId);
                        this._started = true;
                        resolve();
                    })
                        .catch((err) => {
                            reject(err);
                        });
                } else {
                    reject(`CodeCoverageHub: signalR hub is not initialized`);
                }
            }).catch((err) => {
                reject(err);
            });
        });
    }

    public unwatchBuild(buildId: number): Promise<void> {
        const hub = this.hub;
        const watchedBuilds = this._watchedBuilds;
        return new Promise<void>((resolve, reject) => {
            if (hub && watchedBuilds.has(buildId) && this._started) {
                hub.server.unwatchBuild(buildId).then(() => {
                    watchedBuilds.delete(buildId);
                    resolve();
                }).catch((reason: any) => {
                    reject(reason);
                });
            } else {
                return Promise.resolve();
            }
        });
    }

    public dispose() {
        if (this._started) {
            super.dispose();
        }
    }

    public getWatchedBuilds(): Set<number> {
        return this._watchedBuilds;
    }

    private _initializeClient = (proxy: ISignalRHubProxy<ICodeCoverageSignalRHubClient, ICodeCoverageSignalRHubServer>) => {

        proxy.client.codeCoverageStatsChangedForBuild = (buildId: number) => {
            if (this._options && this._options.notificationSync) {
                this._options.notificationSync.codeCoverageStatsChangedForBuild(buildId);
            }
        };
    }
}

export class CodeCoverageSignalRHubService extends VssService implements ICodeCoverageSignalRHubService, ICodeCoverageHubSignalRSync {
    private static s_codeCoverageSignalRHub: CodeCoverageSignalRHub | undefined;
    private static s_notificationCallBacks: ICodeCoverageHubSignalRSync[];
    private static s_notficationCallBacksLength: number;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext)
        CodeCoverageSignalRHubService.s_notificationCallBacks = [];
        CodeCoverageSignalRHubService.s_notficationCallBacksLength = 0;
        CodeCoverageSignalRHubService.s_codeCoverageSignalRHub = undefined;
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
        if (CodeCoverageSignalRHubService.s_codeCoverageSignalRHub) {
            CodeCoverageSignalRHubService.s_codeCoverageSignalRHub.dispose();
        }
        CodeCoverageSignalRHubService.s_notificationCallBacks = []
        CodeCoverageSignalRHubService.s_notficationCallBacksLength = 0;
    }

    public initialize(pageContext: IVssPageContext, projectId: string, notificationSync: ICodeCoverageHubSignalRSync): Promise<void> {
        return new Promise(async (resolve, reject) => {
            const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
            const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
            const vssPageData = vssPageService.getData();
            const signalRData: ISignalRUrlData | undefined = await contributionService.getDataAsync<ISignalRUrlData>("ms.vss-test-web.test-tab-signalr-data-provider", {}, false);

            if (!vssPageData || !signalRData || !notificationSync || !projectId) {
                reject("Invalid parameters.");
                return;
            }

            const hostId = vssPageData.hostId;
            const isHosted = vssPageData.isHosted;
            const hostInformation: IHubHostInformation = {
                hostId: hostId,
                isHosted: isHosted,
                hostPath: signalRData.connectionUrl.split("_apis")[0],
                projectId: projectId
            };

            if (!CodeCoverageSignalRHubService.s_codeCoverageSignalRHub) {
                const signalrHubOptions: ICodeCoverageSignalRHubOptions = {
                    hostInformation: hostInformation,
                    projectId: projectId,
                    notificationSync: this
                };
                CodeCoverageSignalRHubService.s_codeCoverageSignalRHub = new CodeCoverageSignalRHub(signalrHubOptions);
            }
            CodeCoverageSignalRHubService.s_notificationCallBacks.push(notificationSync);
            CodeCoverageSignalRHubService.s_notficationCallBacksLength += 1;
            resolve();
        });
    }

    public watchBuild(buildId: number): Promise<void> {
        if (CodeCoverageSignalRHubService.s_codeCoverageSignalRHub) {
            return CodeCoverageSignalRHubService.s_codeCoverageSignalRHub.watchBuild(buildId);
        } else {
            return Promise.reject(`CodeCoverageHub: CodeCoverageSignalRHub is not initialized`)
        }
    }

    public unwatchBuild(buildId: number): Promise<void> {
        if (CodeCoverageSignalRHubService.s_codeCoverageSignalRHub && CodeCoverageSignalRHubService.s_notficationCallBacksLength == 1) {
            return CodeCoverageSignalRHubService.s_codeCoverageSignalRHub.unwatchBuild(buildId);
        } else {
            return Promise.resolve();
        }
    }

    public dispose() {
        if (!CodeCoverageSignalRHubService.s_codeCoverageSignalRHub) {
            return;
        }

        CodeCoverageSignalRHubService.s_notficationCallBacksLength -= 1;
        if (CodeCoverageSignalRHubService.s_notficationCallBacksLength <= 0) {
            CodeCoverageSignalRHubService.s_codeCoverageSignalRHub.dispose();
            CodeCoverageSignalRHubService.s_codeCoverageSignalRHub = undefined;
            console.log("CodeCoverage: Disconnected")
        }
    }

    public codeCoverageStatsChangedForBuild(buildId: number): void {
        CodeCoverageSignalRHubService.s_notificationCallBacks.forEach(element => {
            try {
                element.codeCoverageStatsChangedForBuild(buildId);
            } catch (error) {
                console.warn("CodeCoverageHub: Error in calling the callback");
            }
        });
    }
}

Services.add("ICodeCoverageSignalRHub", { serviceFactory: CodeCoverageSignalRHubService });
